import { createSelector, createSlice } from "@reduxjs/toolkit";

const initialState = {
    dishSemiFinishedModalArray: [],
    dishSemiFinishedWeight: 0,
    dishSemiFinishedWeightNetto: 0,
    dishSemiFinishedCost: 0,
    dishSemiFinishedEnergy: 0,
    dishSemiFinishedFats: 0,
    dishSemiFinishedProteins: 0,
    dishSemiFinishedCarbohydrates: 0,
    trigger: 0
};

const dishSemiFinishedSlice = createSlice({
    name: 'dishSemiFinishedSlice',
    initialState,
    reducers: {
        changeTrigger: (state) => {
            state.trigger += 1;
        },
        updateArray: (state, action) => {
            state.dishSemiFinishedModalArray = action.payload.map(item => {
                const weight = parseFloat(item.weight) || 0;
                
                // First ensure weightNetto is calculated for display
                let processedItem = { ...item };
                
                // If weightNetto is not defined, calculate it
                if (item.weightNetto === undefined || item.weightNetto === null) {
                    if (item.semiFinished) {
                        const semiWeight = parseFloat(item.semiFinished.weight) || 0;
                        const semiWeightNetto = parseFloat(item.semiFinished.weightNetto) || 0;
                        const nettoRatio = semiWeight > 0 ? semiWeightNetto / semiWeight : 1;
                        processedItem.weightNetto = Number((weight * nettoRatio).toFixed(2));
                    } else {
                        // Fallback - assume weightNetto equals weight
                        processedItem.weightNetto = weight;
                    }
                }

                const weightNetto = parseFloat(processedItem.weightNetto) || 0;

                const get = (field) => {
                    const direct = item[field];
                    if (direct !== undefined && direct !== 0) return parseFloat(direct);
                    const fromSemi = item.semiFinished?.[field];
                    if (fromSemi !== undefined && fromSemi !== 0) return parseFloat(fromSemi);
                    const fromProduct = item.product?.[field];
                    return parseFloat(fromProduct) || 0;
                };

                // Check if this is a semiFinished item 
                if (item.semiFinished) {
                    // API data - semiFinished nutrients are already calculated for the actual weight used
                    return {
                        ...processedItem,
                        energy: Number(parseFloat(item.semiFinished.energy) || 0),
                        fats: Number(parseFloat(item.semiFinished.fats) || 0),
                        proteins: Number(parseFloat(item.semiFinished.proteins) || 0),
                        carbohydrates: Number(parseFloat(item.semiFinished.carbohydrates || item.semiFinished.carbs) || 0),
                    };
                } else {
                    // For product items, apply per-100g calculation
                    return {
                        ...processedItem,
                        energy: Number(((get("energy") * weight) / 100).toFixed(2)),
                        fats: Number(((get("fats") * weight) / 100).toFixed(2)),
                        proteins: Number(((get("proteins") * weight) / 100).toFixed(2)),
                        carbohydrates: Number(((get("carbohydrates") * weight) / 100).toFixed(2)),
                    };
                }
            });

            dishSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        addDishSemiFinishForModal: (state, action) => {
            state.dishSemiFinishedModalArray.push(action.payload);
            dishSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        updateDishSemiFinishedForModal: (state, action) => {
            state.dishSemiFinishedModalArray = action.payload;
            dishSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        removeDishSemiFinishForModal: (state, action) => {
            state.dishSemiFinishedModalArray = state.dishSemiFinishedModalArray.filter(
                (product) => product.id !== action.payload
            );
            dishSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        clearArray: (state) => {
            state.dishSemiFinishedModalArray = [];
            state.dishSemiFinishedWeight = 0;
            state.dishSemiFinishedWeightNetto = 0;
            state.dishSemiFinishedCost = 0;
            state.dishSemiFinishedEnergy = 0;
            state.dishSemiFinishedFats = 0;
            state.dishSemiFinishedProteins = 0;
            state.dishSemiFinishedCarbohydrates = 0;
        },
        recalculateTotals: (state) => {
            const array = state.dishSemiFinishedModalArray;

            state.dishSemiFinishedWeight = array.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0);
            
            state.dishSemiFinishedWeightNetto = array.reduce(
                (sum, product) => {
                    const weight = parseFloat(product.weight) || 0;
                    
                    // Check if this is a manually added item with pre-calculated weightNetto
                    if (product.weightNetto !== undefined) {
                        return sum + (parseFloat(product.weightNetto) || 0);
                    }
                    
                    // Handle API data structure - calculate weightNetto based on semiFinished's ratio
                    if (product.semiFinished) {
                        const semiWeight = parseFloat(product.semiFinished.weight) || 0;
                        const semiWeightNetto = parseFloat(product.semiFinished.weightNetto) || 0;
                        const nettoRatio = semiWeight > 0 ? semiWeightNetto / semiWeight : 0;
                        return sum + (weight * nettoRatio);
                    }
                    
                    // Fallback - assume 100% ratio if no ratio data available
                    return sum + weight;
                }, 0
            );
            
            state.dishSemiFinishedCost = parseFloat(array.reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0).toFixed(2));
            state.dishSemiFinishedEnergy = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.energy !== undefined) {
                        return sum + (parseFloat(product.energy) || 0);
                    }
                    
                    // Handle API data structure - semiFinished values are already calculated for the actual weight used
                    const energy = parseFloat(product.semiFinished?.energy) || 0;
                    return sum + energy;
                }, 0).toFixed(2)
            );
            state.dishSemiFinishedFats = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.fats !== undefined) {
                        return sum + (parseFloat(product.fats) || 0);
                    }
                    
                    // Handle API data structure - semiFinished values are already calculated for the actual weight used
                    const fats = parseFloat(product.semiFinished?.fats) || 0;
                    return sum + fats;
                }, 0).toFixed(2)
            );
            state.dishSemiFinishedProteins = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.proteins !== undefined) {
                        return sum + (parseFloat(product.proteins) || 0);
                    }
                    
                    // Handle API data structure - semiFinished values are already calculated for the actual weight used
                    const proteins = parseFloat(product.semiFinished?.proteins) || 0;
                    return sum + proteins;
                }, 0).toFixed(2)
            );
            state.dishSemiFinishedCarbohydrates = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.carbohydrates !== undefined) {
                        return sum + (parseFloat(product.carbohydrates) || 0);
                    }
                    
                    // Handle API data structure - semiFinished values are already calculated for the actual weight used
                    const carbs = parseFloat(product.semiFinished?.carbohydrates || product.semiFinished?.carbs) || 0;
                    return sum + carbs;
                }, 0).toFixed(2)
            );
        }
    }
});

const arrayFormattedDishSemiFinished = createSelector(
    (state) => state.dishSemiFinished.dishSemiFinishedModalArray,
    (array) =>
        array.map((product, index) => ({
            index: index,
            semiFinishedId: product.semiFinishedId ?? product.id,
            weight: product.weight,
            weightNetto: product.weightNetto,
            cost: product.cost,
            price: product.price,
        }))
);

const { reducer: dishSemiFinishedReducer, actions } = dishSemiFinishedSlice;
const dishSemiFinishedActions = { ...actions };

export {
    dishSemiFinishedReducer,
    dishSemiFinishedActions,
    arrayFormattedDishSemiFinished
};





